import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Upload } from "lucide-react";
import { ApiError, api, type Resume } from "../lib/api.js";
import { Card, CardContent } from "../components/ui/card.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";

const STATUS_VARIANT: Record<Resume["status"], "neutral" | "pending" | "verified" | "claimed" | "revoked"> = {
  uploaded: "neutral",
  parsing: "pending",
  parsed: "verified",
  edited: "claimed",
  failed: "revoked",
};

const ACCEPT = ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Resume ingestion (REQ-2.1–2.3): upload a PDF/DOCX and review the extraction. */
export function ResumesPage() {
  const [resumes, setResumes] = useState<Resume[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  function refresh() {
    void api.listResumes().then((r) => setResumes(r.resumes));
  }
  useEffect(refresh, []);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      await api.uploadResume(file);
      refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Resumes</h1>
        <div>
          <input ref={fileInput} type="file" accept={ACCEPT} className="hidden" onChange={(e) => void onFile(e)} />
          <Button onClick={() => fileInput.current?.click()} disabled={uploading}>
            <Upload size={16} /> {uploading ? "Uploading…" : "Upload resume"}
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {!resumes ? (
        <div className="h-40 animate-pulse rounded-[var(--radius-lg)] bg-secondary" />
      ) : resumes.length === 0 ? (
        <Card>
          <CardContent className="grid place-items-center gap-2 p-12 text-center">
            <FileText size={32} className="text-muted-foreground" />
            <p className="font-medium">No resumes yet</p>
            <p className="text-sm text-muted-foreground">
              Upload a PDF or DOCX resume and we&apos;ll extract your experience, skills, and education.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {resumes.map((r) => (
            <Card key={r.id}>
              <CardContent className="flex items-center gap-4 p-4">
                <FileText size={20} className="shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{r.fileName ?? "Resume"}</p>
                  <p className="text-xs text-muted-foreground">
                    Uploaded {new Date(r.createdAt).toLocaleString()}
                  </p>
                </div>
                <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge>
                <Button asChild size="sm" variant="secondary">
                  <Link to={`/resumes/${r.id}`}>Review</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
